import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { RichTextEditor } from '@/shared/ui/rich-text-editor';
import { Checkbox } from '@/shared/ui/checkbox';
import { useVersionPublish } from '@/modules/agreements/platform/pages/document-detail-page/hooks/use-version-publish';

/**
 * Publish a new version of a document.
 *
 * The server assigns the version number; the client never sends one. `effectiveAt` is when
 * the version becomes binding. Attachments are references to already-uploaded objects, so
 * this form does not upload bytes — it records the reference.
 *
 * The submit's outcome is reported as a notification, so there is no inline error block: a
 * failure does not push the fields down, and the operator's input survives for a retry.
 *
 * Content is authored as rich text; the editor emits the HTML the server stores as the
 * version's content. An empty field is only an error once the operator has acted on it.
 */
export function VersionPublishForm(props: {
  documentId: string;
  onPublished: (versionId: string) => void;
}): ReactNode {
  const form = useVersionPublish(props.documentId, props.onPublished);

  return (
    <div className="space-y-4">
      <Input
        label="Locale"
        value={form.locale}
        onChange={(event) => form.setLocale(event.target.value)}
        hint="e.g. en or en-IN"
        error={
          form.locale.length > 0 && !form.localeValid
            ? 'Must match ^[a-z]{2}(-[A-Z]{2})?$'
            : undefined
        }
      />
      <Checkbox
        label="Default locale"
        checked={form.isDefaultLocale}
        onChange={(event) => form.setIsDefaultLocale(event.target.checked)}
      />
      <Input
        label="Version title"
        value={form.title}
        onChange={(event) => form.setTitle(event.target.value)}
      />
      <RichTextEditor
        label="Content"
        value={form.content}
        onChange={form.setContent}
        isInvalid={form.contentInvalid}
        errorMessage={form.contentInvalid ? 'Content is required.' : undefined}
      />
      <Input
        label="Effective from"
        type="date"
        value={form.effectiveAt}
        onChange={(event) => form.setEffectiveAt(event.target.value)}
      />
      <Button loading={form.busy} disabled={!form.canSubmit} onClick={() => void form.submit()}>
        Publish version
      </Button>
    </div>
  );
}
